import React, { useCallback, useState } from 'react';
import { Prompt } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import { DraftsSelector } from 'features/drafts';
import { NewCandidateDataLoader } from 'features/new-candidate/components';
import {
  CANDIDATE_DRAFTS_COLUMNS,
  NEW_CANDIDATE_ENDPOINTS,
  ORDER_BY_OPTIONS,
  SOURCE_LABELS,
  SOURCES
} from 'features/new-candidate/constants';
import { useLeaveGuard } from 'features/new-candidate/hooks';
import { CandidateQuickEntryTable } from 'features/quick-entry/components';
import LogRocket from 'logrocket';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import ContentPageLayout from 'UI/components/templates/ContentPageLayout';
import { CandidateEntity, ContactRole, EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { LogRocketCustomEvents } from 'UI/constants/LogRocketCustomEvents';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';

import { getInitialFetcherParams, getInitialSource } from './NewCandidateSelector.utils';
import { CreateFromButtons, HeaderButtons, SelectFromDirectory } from './ui';

export const NewCandidateSelector = ({ queryParams, onReset }) => {
  const initialSource = getInitialSource(queryParams);
  const [loaderParams, setLoaderParams] = useState(getInitialFetcherParams(queryParams) || {});
  const [fromSource, setFromSource] = useState(initialSource);
  const [sourceLabel, setSourceLabel] = useState(SOURCE_LABELS?.[initialSource] ?? '');
  const [resetFormCounter, setResetFormCounter] = useState(0);
  const { allowNextNavigation, canDiscardCurrentSelection, getPromptMessage, registerLeaveGuard } =
    useLeaveGuard();

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isQuickEntryEnabled = checkIfFeatureFlagEnabled(FeatureFlags.QuickEntry);
  const isFromScratch = fromSource === SOURCES.FROM_SCRATCH;
  const showSelectFromDirectory = fromSource === SOURCES.FROM_DIRECTORY && !loaderParams?.id;

  const handleClickFrom = source => {
    setFromSource(source);
    setSourceLabel(SOURCE_LABELS?.[source] ?? '');
    LogRocket.track(LogRocketCustomEvents.NewCandidate.SelectSource, { source });
  };

  const handleSelectFromDirectory = value => {
    const roleId = value?.role_id;

    const contactId = roleId === ContactRole.Name ? value?.id : null;
    const hiringId = roleId === ContactRole.HiringAuthority ? value?.id : null;

    setLoaderParams({ ...value, contactId, hiringId });
  };

  const handleSelectFromDraft = value => {
    setLoaderParams({ ...value, isFromDraft: true });
    setFromSource(SOURCES.FROM_DRAFT);
    LogRocket.track(LogRocketCustomEvents.NewCandidate.SelectSource, {
      source: SOURCES.FROM_DRAFT
    });
  };

  const handleResetForm = () => {
    LogRocket.track(LogRocketCustomEvents.NewCandidate.ResetForm);
    if (!canDiscardCurrentSelection()) return;

    setFromSource(SOURCES.FROM_SCRATCH);
    setResetFormCounter(prev => prev + 1);
  };

  const handleResetAll = () => {
    LogRocket.track(LogRocketCustomEvents.NewCandidate.ResetAll);
    if (!canDiscardCurrentSelection()) return;

    allowNextNavigation();
    setFromSource(null);
    setSourceLabel('');
    setLoaderParams({
      applicantId: null,
      company: null,
      contactId: null,
      draftId: null,
      hiringId: null,
      id: null,
      isFromDraft: false,
      resumeId: null,
      source: null
    });
    onReset();
  };

  const updateSelectedFromLabel = useCallback(source => {
    setSourceLabel(SOURCE_LABELS?.[source] ?? '');
  }, []);

  return (
    <ContentPageLayout
      text={`${strings.candidates.creation.titleV2} ${sourceLabel}`}
      titleLabelProps={{
        backNavigation: true,
        onCustomBackClick: handleResetAll,
        mode: fromSource ? 'customAction' : 'goBack'
      }}
    >
      <Prompt when message={getPromptMessage} />

      <When condition={fromSource && isFromScratch}>
        <HeaderButtons onResetForm={handleResetForm} />
      </When>

      <Box marginTop={2}>
        <When condition={!fromSource}>
          <Box alignContent="center" display="flex" flexDirection="column" gridGap={24}>
            <CreateFromButtons onClickFrom={handleClickFrom} />
            <DraftsSelector
              columns={CANDIDATE_DRAFTS_COLUMNS}
              endpoint={NEW_CANDIDATE_ENDPOINTS.drafts}
              entity={EntityType.Candidate}
              entityLabel={CandidateEntity.singular}
              onSelectDraft={handleSelectFromDraft}
              orderByOptions={ORDER_BY_OPTIONS}
              pageKey={PERSISTENCE_KEYS.CREATION.CANDIDATE_DRAFTS}
            />

            <When condition={isQuickEntryEnabled}>
              <CandidateQuickEntryTable />
            </When>
          </Box>
        </When>

        <When condition={showSelectFromDirectory}>
          <SelectFromDirectory onSelect={handleSelectFromDirectory} />
        </When>

        <When condition={fromSource && !showSelectFromDirectory}>
          <NewCandidateDataLoader
            key={resetFormCounter}
            loaderParams={loaderParams}
            fromSource={fromSource}
            onLoad={updateSelectedFromLabel}
            onRegisterLeaveGuard={registerLeaveGuard}
            preselectedCompany={loaderParams?.company}
          />
        </When>
      </Box>
    </ContentPageLayout>
  );
};
